import { ArrowLeft, ExternalLink, TrendingUp, Zap, FileCheck, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import '@/styles/ProductPage.css';

const Straly = () => {
  return (
    <div className="product-page straly-page">
      {/* Header */}
      <header className="product-header" data-testid="product-header">
        <nav className="product-nav" data-testid="product-nav">
          <Button 
            onClick={() => window.location.href = '/'} 
            variant="ghost" 
            className="back-btn"
            data-testid="back-button"
          >
            <ArrowLeft size={20} /> Back to Home
          </Button>
        </nav>
      </header>

      {/* Hero */}
      <section className="product-hero straly-hero" data-testid="hero-section">
        <h1 className="product-hero-title" data-testid="hero-title">Expert Marketing Strategy</h1>
        <p className="product-hero-tagline" data-testid="hero-tagline">
          No marketing experience? No problem.
        </p>
        <p className="hero-description" data-testid="hero-description">
          Answer a few simple questions about your business. Straly creates a growth plan just for you. 
          The plan uses 8+ years of research. It's tested with real small businesses like yours.
        </p>
        <div className="hero-stats" data-testid="hero-stats">
          <div className="stat" data-testid="stat-growth">
            <TrendingUp size={32} />
            <span className="stat-value">30%</span>
            <span className="stat-label">Revenue Growth</span>
          </div>
          <div className="stat" data-testid="stat-time">
            <Zap size={32} />
            <span className="stat-value">6 Months</span>
            <span className="stat-label">To Results</span>
          </div>
          <div className="stat" data-testid="stat-research">
            <FileCheck size={32} />
            <span className="stat-value">8+ Years</span>
            <span className="stat-label">Research Backed</span>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="product-features" data-testid="features-section">
        <div className="container">
          <h2 className="features-title" data-testid="features-title">What You'll Get</h2>
          <div className="features-grid">
            <div className="feature-item" data-testid="feature-audit">
              <div className="feature-icon straly-icon">
                <FileCheck size={48} />
              </div>
              <h3>1-Click Audit & Review</h3>
              <p>Get instant feedback on your marketing strategy. We run checks with our research on what works so you don't waste time or make costly mistakes.</p>
            </div>

            <div className="feature-item" data-testid="feature-personalized">
              <div className="feature-icon straly-icon">
                <Sparkles size={48} />
              </div>
              <h3>Personalized Strategy</h3>
              <p>Our proprietary AI logic creates what you actually need—tested to work. No generic tools, no recycled templates.</p>
            </div>

            <div className="feature-item" data-testid="feature-assets">
              <div className="feature-icon straly-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <line x1="9" y1="9" x2="15" y2="9" />
                  <line x1="9" y1="15" x2="15" y2="15" />
                </svg>
              </div>
              <h3>Ready-to-Use Assets</h3>
              <p>From business names to websites, pricing strategy and social content—get ready-to-use material built around your business right now.</p>
            </div>

            <div className="feature-item" data-testid="feature-science">
              <div className="feature-icon straly-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 11l3 3L22 4" />
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
                </svg>
              </div>
              <h3>Science-Based AI</h3>
              <p>Powered by 8+ years of exclusive research and in-market testing by a Doctor of Business Administration in Marketing Strategy.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Business Types */}
      <section className="benefits-section" data-testid="benefits-section">
        <div className="container">
          <h2 className="benefits-title" data-testid="benefits-title">Built for Your Business</h2>
          <p className="benefits-subtitle">Whether you run a shop, provide a service, or sell online, our app creates a plan proven to work for small businesses, just like yours.</p>
          <div className="benefits-grid">
            <div className="benefit-item" data-testid="benefit-shops">
              <div className="benefit-icon straly-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                  <polyline points="9 22 9 12 15 12 15 22" />
                </svg>
              </div>
              <h3>Shops & Restaurants</h3>
              <p>Get more customers and keep them coming back.</p>
            </div>
            <div className="benefit-item" data-testid="benefit-services">
              <div className="benefit-icon straly-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
              </div>
              <h3>Services</h3>
              <p>Land more jobs, from cleaning to consulting.</p>
            </div>
            <div className="benefit-item" data-testid="benefit-online">
              <div className="benefit-icon straly-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="2" y1="12" x2="22" y2="12" />
                  <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
                </svg>
              </div>
              <h3>Online Stores</h3>
              <p>Increase sales without the hassle.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="testimonial-section" data-testid="testimonial-section">
        <div className="container">
          <div className="testimonial straly-testimonial">
            <p className="testimonial-text" data-testid="testimonial-text">
              "My marketing strategy was so successful for my cleaning business I used it to start my dog training business!"
            </p>
            <p className="testimonial-author" data-testid="testimonial-author">- Kat, Spotless Cleanrz and Fetch Pups</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section straly-cta-section" data-testid="cta-section">
        <h2 className="cta-title" data-testid="cta-title">Ready to Grow Your Business?</h2>
        <p className="cta-description" data-testid="cta-description">
          Get started for free with 5 monthly steps. Upgrade to Full Access to unlock your complete strategy.
        </p>
        <div className="cta-buttons" data-testid="cta-buttons">
          <a 
            href="https://apps.apple.com/app/straly/id6759396728" 
            target="_blank" 
            rel="noopener noreferrer"
            className="cta-button straly-cta-btn"
            data-testid="cta-app-store"
          >
            <ExternalLink size={20} /> Download on the App Store
          </a>
          <a 
            href="https://play.google.com/store/apps/details?id=com.straly.app" 
            target="_blank" 
            rel="noopener noreferrer"
            className="cta-button straly-cta-btn"
            data-testid="cta-google-play"
          >
            <ExternalLink size={20} /> Download on Google Play
          </a>
        </div>
        <p className="cta-pricing" data-testid="cta-pricing">
          Start free · Premium plans for micro businesses
        </p>
      </section>
    </div>
  );
};

export default Straly;
